import { Modal } from "./Modal";

type Props<T> = {
  formData: T;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
};

export function TesterModal({
  formData,
  onChange,
  onClose,
  onSubmit,
  isSubmitting
}: Props<{
  first_name: string;
  last_name: string;
  email: string;
  sport_focus: string[];
  hardware: string[];
  other_hardware?: string;
  polar_product?: string;
  garmin_product?: string;
}>) {
  const handleCheckboxGroupChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "sport_focus" | "hardware"
  ) => {
    const { value } = e.target;
    const prevValues = formData[field];
    const updatedValues = prevValues.includes(value)
      ? prevValues.filter((v) => v !== value)
      : [...prevValues, value];

    const syntheticEvent = {
      target: {
        name: field,
        value: updatedValues,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent); // delegates to your shared onChange
  };

  return (
    <Modal title="Join as a Tester" onClose={onClose}>
      <p className="mb-4">
        Try MiValta before the world does. Help shape our AI coach with real
        feedback.
      </p>
      <form className="flex flex-col gap-2" onSubmit={onSubmit}>
        <input
          name="first_name"
          placeholder="First Name"
          value={formData.first_name}
          onChange={onChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black"
          required
        />
        <input
          name="last_name"
          placeholder="Last Name"
          value={formData.last_name}
          onChange={onChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black"
          required
        />
        <input
          type="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={onChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black"
          required
        />

        <fieldset className="border border-gray-200 rounded-md p-3">
          <legend className="text-sm font-semibold text-gray-700 mb-2">
            Sport Focus
          </legend>
          {["Running", "Cycling"].map((option) => (
            <label
              key={option}
              className="flex items-center gap-2 text-sm text-gray-800 mb-1"
            >
              <input
                type="checkbox"
                name="sport_focus"
                value={option}
                checked={formData.sport_focus.includes(option)}
                onChange={(e) => handleCheckboxGroupChange(e, "sport_focus")}
              />
              {option}
            </label>
          ))}
        </fieldset>

        <fieldset className="border border-gray-200 rounded-md p-3">
          <legend className="text-sm font-semibold text-gray-700 mb-2">
            Hardware
          </legend>
          {["Polar", "Garmin", "Other"].map((option) => (
            <label
              key={option}
              className="flex items-center gap-2 text-sm text-gray-800 mb-1"
            >
              <input
                type="checkbox"
                name="hardware"
                value={option}
                checked={formData.hardware.includes(option)}
                onChange={(e) => handleCheckboxGroupChange(e, "hardware")}
              />
              {option}
            </label>
          ))}
        </fieldset>

        {formData.hardware.includes("Polar") && (
          <input
            type="text"
            name="polar_product"
            placeholder="Which Polar product(s)?"
            value={formData.polar_product || ""}
            onChange={onChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black"
          />
        )}

        {formData.hardware.includes("Garmin") && (
          <input
            type="text"
            name="garmin_product"
            placeholder="Which Garmin product(s)?"
            value={formData.garmin_product || ""}
            onChange={onChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black"
          />
        )}

        {formData.hardware.includes("Other") && (
          <input
            type="text"
            name="other_hardware"
            placeholder="Please list the other brands"
            value={formData.other_hardware || ""}
            onChange={onChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black"
          />
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-4 py-2 text-sm bg-[#23c8a1] text-white rounded hover:brightness-110 transition ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
